/**
 * ROOF FRAMING ENVELOPE (2026-10-14, Matt: "the rafter settings aren't reflected with this
 * hatch … it shows the same main body framing depth all the way to the outer eave/rake
 * extent … where we use overframing or join the roof … the shaded region also doesn't
 * reflect this"). A framed roof's body is hollow — the rafters stand in for its core — so a
 * section cut landing BETWEEN rafters reads the core through the light ASSEMBLY DEPTH tone
 * (section.ts). That tone used to be the whole face ring at the full core depth: blind to the
 * eave crop, the birdsmouth + plumb cut on a tails face, the tails' own depth and end profile,
 * the barge rake (no field rafters past the gable line), and to the overframe / vertical-cut
 * trims (it re-solved the roof with only the join applied).
 *
 * The fix: framing.ts records, alongside the members it builds, the region the members of
 * each kind would sweep if they stood at every station — the SAME closures, rings and
 * profiles the members use, on the SAME post-overframe/join/cut faces, so the tone and the
 * framing cannot drift. Plain data (no closures), so it survives any copy of the solid.
 *
 * Two piece kinds:
 *  - 'plane': members whose top/bottom ride the face plane (field rafters and their eave
 *    crop / birdsmouth seat). z(p) = z0 + g·(p − origin); top = z + topOff; bottom =
 *    min(max(z + botOff, botFloor), top).
 *  - 'profile': members with a fixed section outline in (a, z), a measured along the plan
 *    direction `d` from `origin`, swept across `ring` (tails, corbels, soffit lookouts).
 */
import type { Pt2 } from '@aj/bim-kernel'
import { intersectClosed, signedArea } from '../../clipper'

export type RoofEnvelopePiece =
  | {
      kind: 'plane'
      ring: Pt2[]
      origin: Pt2
      z0: number
      g: Pt2
      botOff: number
      topOff: number
      botFloor?: number
    }
  | {
      kind: 'profile'
      ring: Pt2[]
      origin: Pt2
      d: Pt2
      outline: [number, number][]
    }

/** a face plane as plain data, read off its own z function (planar → affine) */
export function planeOf(zAt: (p: Pt2) => number, origin: Pt2): { origin: Pt2; z0: number; g: Pt2 } {
  const z0 = zAt(origin)
  return {
    origin: [origin[0], origin[1]],
    z0,
    g: [zAt([origin[0] + 1, origin[1]]) - z0, zAt([origin[0], origin[1] + 1]) - z0],
  }
}

const EPS = 1e-9

/**
 * The envelope's section at a vertical cut along the line A + dhat·u, u ∈ [0, len], as
 * (u, z) polygons. `crossings(ring)` = the sorted u's where a plan ring crosses the line
 * (section.ts's own ringCrossings, so the tone pairs intervals exactly like every other cut).
 */
export function envelopeCutPolys(
  pieces: readonly RoofEnvelopePiece[], A: Pt2, dhat: Pt2, len: number,
  crossings: (ring: Pt2[]) => number[],
): Pt2[][] {
  const out: Pt2[][] = []
  for (const pc of pieces) {
    if (pc.ring.length < 3) continue
    const cu = crossings(pc.ring)
    for (let i = 0; i + 1 < cu.length; i += 2) {
      const u0 = Math.max(0, cu[i]), u1 = Math.min(len, cu[i + 1])
      if (u1 - u0 < 1e-6) continue
      if (pc.kind === 'plane') {
        // z along the line is linear in u: z(u) = zA + s·u
        const zA = pc.z0 + pc.g[0] * (A[0] - pc.origin[0]) + pc.g[1] * (A[1] - pc.origin[1])
        const s = pc.g[0] * dhat[0] + pc.g[1] * dhat[1]
        const top = (u: number) => zA + s * u + pc.topOff
        const bot = (u: number) => Math.min(Math.max(zA + s * u + pc.botOff, pc.botFloor ?? -Infinity), top(u))
        const us = [u0, u1]
        // the clamped bottom kinks where the plane meets the floor (and where it would
        // meet the top) — exact vertices, no sampling
        if (pc.botFloor != null && Math.abs(s) > EPS) {
          for (const off of [pc.botOff, pc.topOff]) {
            const uk = (pc.botFloor - zA - off) / s
            if (uk > u0 + 1e-6 && uk < u1 - 1e-6) us.push(uk)
          }
        }
        us.sort((x, y) => x - y)
        const poly: Pt2[] = [
          ...us.map((u): Pt2 => [u, top(u)]),
          ...[...us].reverse().map((u): Pt2 => [u, bot(u)]),
        ]
        if (Math.abs(signedArea(poly)) > 1e-8) out.push(signedArea(poly) > 0 ? poly : poly.reverse())
        continue
      }
      // profile: a(u) = aA + k·u along the cut
      const aA = (A[0] - pc.origin[0]) * pc.d[0] + (A[1] - pc.origin[1]) * pc.d[1]
      const k = dhat[0] * pc.d[0] + dhat[1] * pc.d[1]
      if (Math.abs(k) > 1e-6) {
        // the outline maps affinely into (u, z); keep the stretch inside this crossing
        let mapped: Pt2[] = pc.outline.map(([a, z]): Pt2 => [(a - aA) / k, z])
        if (signedArea(mapped) < 0) mapped = mapped.reverse()
        let zLo = Infinity, zHi = -Infinity
        for (const p of mapped) { if (p[1] < zLo) zLo = p[1]; if (p[1] > zHi) zHi = p[1] }
        const box: Pt2[] = [[u0, zLo - 1], [u1, zLo - 1], [u1, zHi + 1], [u0, zHi + 1]]
        for (const r of intersectClosed([mapped], [box])) {
          if (r.length >= 3 && Math.abs(signedArea(r)) > 1e-8) out.push(signedArea(r) > 0 ? r : r.reverse())
        }
        continue
      }
      // the cut runs ACROSS the members (along the plate): one station of the outline,
      // its vertical slice(s) held over the whole crossing
      const zs: number[] = []
      const o = pc.outline
      for (let p = 0, q = o.length - 1; p < o.length; q = p++) {
        const [ap, zp] = o[p], [aq, zq] = o[q]
        if ((ap > aA) === (aq > aA)) continue
        zs.push(zq + (zp - zq) * ((aA - aq) / (ap - aq)))
      }
      zs.sort((x, y) => x - y)
      for (let j = 0; j + 1 < zs.length; j += 2) {
        if (zs[j + 1] - zs[j] < 1e-6) continue
        out.push([[u0, zs[j]], [u1, zs[j]], [u1, zs[j + 1]], [u0, zs[j + 1]]])
      }
    }
  }
  return out
}
